/**
 * Supabank Admin Design System - Semantic Token SSOT
 * 
 * This file defines the semantic token system for the admin interface.
 * All components MUST use these tokens instead of raw Tailwind classes.
 * 
 * Principle: Semantic names (fg.default) over raw values (gray-900)
 */

export const semanticTokens = {
  // Foreground Colors (Text)
  // Using CSS variables for automatic dark mode support
  fg: {
    default: 'text-foreground',                           // Primary text (auto dark mode)
    muted: 'text-muted-foreground',                       // Secondary text (auto dark mode)
    subtle: 'text-muted-foreground/80',                   // Tertiary text (auto dark mode)
    inverted: 'text-primary-foreground',                  // Text on dark backgrounds
    disabled: 'text-muted-foreground/50',                 // Disabled state text
  },

  // Background Colors
  // Using CSS variables for automatic dark mode support
  bg: {
    canvas: 'bg-background',                              // App background (auto dark mode)
    surface: 'bg-card',                                   // Card/panel background (auto dark mode)
    raised: 'bg-popover',                                 // Elevated surfaces (auto dark mode)
    muted: 'bg-muted',                                    // Subdued backgrounds (auto dark mode)
    disabled: 'bg-muted/50',                              // Disabled state backgrounds
  },

  // Accent & Status Colors
  // Using CSS variables for automatic dark mode support
  accent: {
    primary: 'bg-primary',                               // Primary actions background only (combine with fg.inverted)
    secondary: 'bg-secondary',                           // Secondary actions (auto dark mode)
    hover: 'bg-primary/90',                              // Primary hover state (auto dark mode)
    fg: 'text-primary',                                  // Accent text (auto dark mode)
    bg: 'bg-primary',                                    // Accent background (auto dark mode)
    border: 'border-primary',                            // Accent border (auto dark mode)

    // Interactive hover states (soft purple/lavender accent)
    // Uses CSS variables for automatic dark mode
    interactive: 'bg-accent/50',                         // Soft hover background (auto dark mode)
    interactiveHover: 'hover:bg-accent/50 hover:text-accent-foreground', // Full hover state
    interactiveText: 'text-accent-foreground',           // Text color on accent background
    // Full-width interactive items (hover до краев контейнера)
    // Use for list items in Card with CardContent p-0
    interactiveItemFullWidth: 'hover:bg-accent/50 hover:text-accent-foreground', // Full-width hover state (wrapper level)
  },

  // Button-specific tokens (FULL button styling with correct text colors)
  // Use these for CTA buttons instead of combining accent.primary + fg.inverted manually
  button: {
    primary: 'bg-primary text-primary-foreground',       // Primary CTA: blue bg + white text
    primaryHover: 'hover:bg-primary/90',                 // Primary hover state
    secondary: 'bg-secondary text-secondary-foreground', // Secondary button
    secondaryHover: 'hover:bg-secondary/80',             // Secondary hover
  },

  positive: {
    bg: 'bg-green-50 dark:bg-green-950/30',              // Success backgrounds
    border: 'border-green-200 dark:border-green-800',    // Success borders
    fg: 'text-green-700 dark:text-green-400',            // Success text
    accent: 'text-green-500 dark:text-green-400',        // Success accents
  },

  // Warning: Sophisticated Warm (Canon v1.4.4)
  // Warm tones signal "attention" without harsh yellow
  // Dark text keeps it premium and readable
  warning: {
    bg: 'bg-amber-50/50 dark:bg-amber-950/50',           // Subtle warm background (increased opacity for dark theme visibility)
    border: 'border-amber-300/70 dark:border-amber-700/70', // Premium warm border - more visible, sophisticated (increased saturation and opacity)
    fg: 'text-slate-800 dark:text-amber-100',            // Dark text (sophisticated) - brighter for dark theme
    accent: 'text-amber-600/80 dark:text-amber-400/80',  // Subtle warm accent
    icon: '[&>svg]:text-amber-600/80 dark:[&>svg]:text-amber-400/80',   // Icon color for SVG elements in Alert
    dot: 'bg-amber-500/80 dark:bg-amber-400/80',         // Indicator dot
  },

  danger: {
    bg: 'bg-red-50 dark:bg-red-950/30',                  // Error/danger backgrounds
    border: 'border-red-200 dark:border-red-800',        // Error borders
    fg: 'text-red-800 dark:text-red-300',                // Error text
    accent: 'text-red-600 dark:text-red-400',            // Error accents
  },

  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',                // Informational backgrounds
    border: 'border-blue-200 dark:border-blue-800',      // Informational borders
    fg: 'text-blue-600 dark:text-blue-400',              // Informational text
    accent: 'text-blue-500 dark:text-blue-400',          // Informational accents
  },

  // Premium: Black Luxury (Canon v1.4.4)
  // Black = exclusive, VIP, premium tier
  // "Премиум и VIP — это больше про черный" - G
  premium: {
    bg: 'bg-slate-900 dark:bg-slate-950',               // Solid luxury black
    bgGradient: 'bg-gradient-to-br from-slate-900 to-slate-800', // Premium gradient
    border: 'border-slate-700 dark:border-slate-600',   // Subtle metallic border
    fg: 'text-white',                                   // White text on black
    accent: 'text-slate-300 dark:text-slate-400',       // Silver accent
    muted: 'text-slate-400 dark:text-slate-500',        // Muted silver
  },

  // Cloud Dancer / Zen State (Canon v1.4.5)
  // Pantone 2026 Color of the Year: Cloud Dancer 11-4201
  // "Состояние воздуха, света, лёгкости, чистого будущего" - G
  // Use for: onboarding, empty states, success screens, KYC verified, premium welcome
  zen: {
    // Core Cloud Dancer palette
    cloudDancer: 'bg-[#F0EFEA]',                        // Primary zen background
    airWhite: 'bg-[#F8F7F4]',                           // Elevated zen surface
    // Brand glyphs on zen backgrounds
    shadowPine: 'text-[#132B24]',                       // S glyph color
    obsidian: 'text-[#0D1F1A]',                         // "supabank" text
    mossedStone: 'text-[#8A9A8B]',                      // "xyz" text (muted)
    // Functional tokens (CSS utility classes auto-switch light/dark)
    bg: 'bg-zen',                                       // Zen background (auto light/dark via CSS utility)
    surface: 'bg-[#F8F7F4] dark:bg-card',               // Zen surface (semantic token)
    fg: 'text-zen',                                     // Primary text on zen (auto light/dark via CSS utility)
    muted: 'text-[#8A9A8B] dark:text-muted-foreground', // Secondary text on zen (semantic token)
    border: 'border-[#E5E4DF] dark:border-border',     // Subtle zen border (semantic token)
    // Cloud gradient (light through clouds)
    gradient: 'bg-gradient-to-br from-[hsl(210,33%,96%)] to-[hsl(225,28%,82%)]',
    gradientSubtle: 'bg-gradient-to-br from-[#F8F7F4] to-[#F0EFEA]',
  },

  // Border Colors
  // Using CSS variables for automatic dark mode support
  border: {
    default: 'border-border',                              // Default borders (auto dark mode)
    subtle: 'border-border/50',                            // Subtle borders (auto dark mode)
    strong: 'border-border',                               // Emphasis borders (auto dark mode)
    focus: 'ring-ring',                                    // Focus ring color (auto dark mode)
  },

  // Focus Ring Tokens (Canon: ring-2 ring-inset - never extends beyond container)
  focus: {
    ring: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60',
    ringWidth: 'ring-2',
    ringColor: 'ring-primary/60',
    ringInset: 'ring-inset',
  },

  // Typography Scale
  typography: {
    // ============================================
    // DISPLAY (Satoshi Black) - Full-width Hero & Impact
    // Use for: Landing pages, marketing, full-width sections
    // ============================================
    displayHero: 'font-display text-display-hero', // 128px - Landing hero XL screens
    display3xl: 'font-display text-display-3xl',   // 96px - Landing hero large screens
    display2xl: 'font-display text-display-2xl',   // 72px - Landing hero
    displayXl: 'font-display text-display-xl',     // 60px - Section hero
    displayLg: 'font-display text-display-lg',     // 48px - Feature hero
    displayMd: 'font-display text-display-md',     // 36px - Card hero
    displaySm: 'font-display text-display-sm',     // 30px - Small display

    // ============================================
    // HERO RESPONSIVE (Satoshi Black) - 2-column layouts
    // Use for: Landing hero with side content (e.g. GlobalRails card)
    // Sized to fit "Send Crypto. Get Cash. Automatically." at 50% width
    // ============================================
    heroXs: 'font-display text-hero-xs',           // 36px - Mobile 375px
    heroSm: 'font-display text-hero-sm',           // 44px - SM 640px
    heroMd: 'font-display text-hero-md',           // 52px - MD 768px
    heroLg: 'font-display text-hero-lg',           // 60px - LG 1024px
    heroXl: 'font-display text-hero-xl',           // 68px - XL 1280px
    hero2xl: 'font-display text-hero-2xl',         // 76px - 2XL 1400px

    // ============================================
    // HEADINGS (Satoshi Bold/SemiBold)
    // ============================================
    h1: 'font-display text-heading-xl',           // 32px - Page titles
    h2: 'font-display text-heading-lg',           // 24px - Section headlines
    h3: 'font-display text-heading-md',           // 20px - Subsection headlines
    h4: 'font-display text-heading-sm',           // 18px - Card titles
    h5: 'font-display text-heading-xs',           // 16px - Small headings
    h6: 'font-display text-heading-xxs',          // 14px - Micro headings

    // ============================================
    // BODY TEXT (Inter Regular)
    // ============================================
    bodyLarge: 'font-body text-body-lg',          // 18px - Lead paragraphs
    body: 'font-body text-body-md',               // 16px - Default body
    bodySmall: 'font-body text-body-sm',          // 14px - Compact body

    // ============================================
    // UI TEXT (Inter Medium)
    // ============================================
    label: 'font-body text-ui-md',                // 14px - Form labels
    buttonText: 'font-body text-ui-md',           // 14px - Button text
    buttonSmall: 'font-body text-ui-sm',          // 13px - Small buttons

    // ============================================
    // CAPTIONS (Inter Regular)
    // ============================================
    caption: 'font-body text-caption-md',         // 13px - Secondary info
    captionSmall: 'font-body text-caption-sm',    // 12px - Timestamps, metadata
    captionXs: 'font-body text-caption-xs',       // 11px - Micro text
    captionMicro: 'font-body text-[10px]',        // 10px - Ultra-micro (nav labels, compact badges)

    // ============================================
    // SPECIAL
    // ============================================
    code: 'font-mono text-sm',                    // Monospace code
    numbers: 'font-body tabular-nums',            // Tabular figures for data

    // ============================================
    // LEGACY ALIASES (for backward compatibility)
    // ============================================
    // These map old tokens to new system
    heading1: 'font-display text-heading-xl',
    heading2: 'font-display text-heading-lg',
    heading3: 'font-display text-heading-md',
    linkText: 'text-sm text-primary hover:text-primary/80',  // Auto dark mode
  },

  // Spacing Scale (margin/padding)
  spacing: {
    xs: 'p-1',      // 4px
    sm: 'p-2',      // 8px
    smPlus: 'p-2.5', // 10px - для точных дизайн-значений (wizard icons, compact elements)
    smMd: 'p-3',     // 12px - промежуточное значение между sm и md
    md: 'p-4',      // 16px
    lg: 'p-6',      // 24px
    xl: 'p-8',      // 32px
    '2xl': 'p-12',  // 48px
  },

  // Margin Scale (directional margins)
  // Use these instead of raw mt-*, mb-*, ml-*, mr-* classes
  margin: {
    // Top margins
    top: {
      xs: 'mt-1',     // 4px
      sm: 'mt-2',     // 8px
      md: 'mt-4',     // 16px
      lg: 'mt-6',     // 24px
      xl: 'mt-8',     // 32px
    },
    // Bottom margins
    bottom: {
      xs: 'mb-1',     // 4px
      sm: 'mb-2',     // 8px
      md: 'mb-4',     // 16px
      lg: 'mb-6',     // 24px
    },
    // Left margins
    left: {
      xs: 'ml-1',     // 4px
      sm: 'ml-2',     // 8px
      md: 'ml-4',     // 16px
      lg: 'ml-6',     // 24px
    },
    // Right margins
    right: {
      xs: 'mr-1',     // 4px
      sm: 'mr-2',     // 8px
      md: 'mr-4',     // 16px
    },
  },

  // Padding Scale (directional padding for specific cases)
  // Use spacing.* for uniform padding, these for directional
  padding: {
    top: {
      xs: 'pt-1',     // 4px
      sm: 'pt-2',     // 8px
      md: 'pt-4',     // 16px
    },
    bottom: {
      xs: 'pb-1',     // 4px
      sm: 'pb-2',     // 8px
      md: 'pb-4',     // 16px
      lg: 'pb-6',     // 24px - для вертикальных отступов (wizard content)
    },
    // Vertical padding (py-*)
    vertical: {
      md: 'py-4',     // 16px
      lg: 'py-6',     // 24px - для wizard content areas
    },
  },

  // Size Scale (width/height for icons, dots, etc.)
  size: {
    // Status indicator dots
    dot: {
      sm: 'w-2 h-2',      // 8px - status dots
      md: 'w-3 h-3',      // 12px - larger indicators
    },
    // Icon sizes (for buttons, inputs)
    icon: {
      xs: 'h-3 w-3',      // 12px - micro icons
      sm: 'h-4 w-4',      // 16px - small icons
      md: 'h-5 w-5',      // 20px - default icons
      lg: 'h-6 w-6',      // 24px - large icons
    },
  },

  // Gap Scale (for flex/grid layouts)
  gap: {
    xs: 'gap-1',      // 4px
    sm: 'gap-2',      // 8px
    smMd: 'gap-3',    // 12px - промежуточное значение (wizard layouts, card spacing)
    md: 'gap-4',      // 16px
    lg: 'gap-6',      // 24px
    xl: 'gap-8',      // 32px
    '2xl': 'gap-12',  // 48px
  },

  // Stack Scale (vertical spacing between elements)
  stack: {
    xs: 'space-y-1',      // 4px
    sm: 'space-y-2',      // 8px
    md: 'space-y-4',      // 16px
    lg: 'space-y-6',      // 24px
    xl: 'space-y-8',      // 32px
    '2xl': 'space-y-12',  // 48px
  },

  // Border Radius
  radius: {
    none: 'rounded-none',
    sm: 'rounded-sm',       // 2px
    md: 'rounded-md',       // 6px
    lg: 'rounded-lg',       // 8px
    xl: 'rounded-xl',       // 12px
    full: 'rounded-full',
  },

  // Box Shadows
  shadow: {
    none: 'shadow-none',
    sm: 'shadow-sm',        // Subtle shadow
    md: 'shadow-md',        // Default shadow
    lg: 'shadow-lg',        // Pronounced shadow
    xl: 'shadow-xl',        // Heavy shadow
    focus: 'ring-2 ring-blue-500 ring-opacity-25', // Focus ring
  },

  // Z-Index Layers
  zIndex: {
    base: 'z-0',           // Base layer
    raised: 'z-10',        // Raised elements
    overlay: 'z-20',       // Overlays, dropdowns
    modal: 'z-30',         // Modals, dialogs
    popover: 'z-40',       // Popovers, tooltips
    toast: 'z-[100]',      // Toast notifications (above modals/menus for mobile visibility)
  },

  // Modal/Dialog specific tokens
  modal: {
    maxHeight: 'max-h-[90vh]',  // Standard modal max height (viewport-relative)
    overflow: 'overflow-y-auto', // Scrollable content when exceeds max height
  },

  // Toast Component specific tokens
  // Duration, positioning, spacing, and behavior tokens for toast notifications
  toast: {
    // Duration (milliseconds)
    duration: 5000,  // Default toast display duration (5 seconds)

    // Swipe direction (mobile-optimized)
    swipeDirection: 'right' as const,  // Swipe right to dismiss (iOS-style, works on mobile)

    // Viewport positioning and spacing (mobile-first canonical)
    viewport: {
      // Position: mobile (top, full width) → desktop (bottom-right, fixed width)
      // Mobile: top-0 для лучшей видимости, desktop: bottom-right для неинтрузивности
      position: 'fixed top-0 left-0 right-0 sm:top-auto sm:left-auto sm:bottom-0 sm:right-0',
      // Z-index (from semanticTokens.zIndex.toast = 'z-[100]')
      zIndex: 'z-[100]',  // SSOT: matches semanticTokens.zIndex.toast (above modals/menus)
      // Layout: mobile (column-reverse для новых тостов сверху) → desktop (column)
      layout: 'flex max-h-screen w-full flex-col-reverse sm:flex-col',
      // Spacing: mobile (p-4 для touch targets) → desktop (p-4)
      padding: 'p-4',  // 16px padding (matches semanticTokens.spacing.md)
      // Max width: mobile (full width) → desktop (canonical 400px)
      maxWidth: 'w-full sm:max-w-[400px]',  // Canonical max-width from SupaToast
      // Pointer events: allow interaction even when menu is open
      pointerEvents: 'pointer-events-auto',  // Ensure toasts are always interactive
    },

    // Toast item styling (Liquid Glass Canon)
    item: {
      // Base styling - Liquid Glass effect (canonical from SupaToastShowcase)
      base: 'group pointer-events-auto relative flex w-full items-start justify-between gap-3 overflow-hidden rounded-2xl border p-4 pr-10 shadow-xl shadow-black/5 ring-1 ring-inset ring-white/20 touch-manipulation',
      // Liquid Glass background (TRUE transparency)
      glass: 'bg-white/50 dark:bg-slate-900/50 backdrop-blur-2xl backdrop-saturate-[180%]',
      // Spacing between toast items
      gap: 'gap-3',  // 12px gap (canonical from SupaToast)
      // Padding
      padding: 'p-4 pr-10',  // 16px padding, 40px right padding for close button (canonical)
      // Border radius (canonical)
      radius: 'rounded-2xl',  // 16px (canonical from SupaToast, not rounded-md)
      // Shadow (canonical)
      shadow: 'shadow-xl shadow-black/5',  // Liquid Glass shadow (canonical)
      // Ring (canonical)
      ring: 'ring-1 ring-inset ring-white/20',  // Glass-like ring
      // Width constraints (canonical)
      width: 'w-full max-w-[400px] min-w-[320px]',  // Canonical sizes from SupaToast
      // Transition for swipe gestures
      swipeTransition: 'transition-transform data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
    },

    // Close button styling (canonical from SupaToast)
    close: {
      // Position (canonical: centered vertically, right-3)
      position: 'absolute top-1/2 right-3 -translate-y-1/2',
      // Size and padding (canonical: p-1.5 для лучшего touch target)
      size: 'p-1.5',  // 6px padding (canonical, лучше для touch)
      // Border radius (canonical: rounded-full)
      radius: 'rounded-full',  // Full circle (canonical from SupaToast)
      // Background (canonical: glass-like)
      bg: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10',
      // Opacity states (canonical: всегда видимый на мобильных)
      opacity: 'opacity-100 sm:opacity-0 sm:transition-opacity sm:focus:opacity-100 sm:group-hover:opacity-100',
      // Focus ring
      focus: 'focus:outline-none focus:ring-2',
    },

    // Action button styling
    action: {
      // Base styling
      base: 'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium',
      // Hover and focus states
      interactive: 'ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      // Disabled state
      disabled: 'disabled:pointer-events-none disabled:opacity-50',
      // Destructive variant styling
      destructive: 'group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
    },
  },

  // Motion & Animation (Page Contract Canon: 60/120/240/480ms)
  motion: {
    // Duration Scale (canonical): 60 / 120 / 240 / 480 ms
    // Easing (unified): cubic-bezier(0.25, 1, 0.5, 1) = ease-standard

    // Micro-interactions (hover states, focus transitions)
    micro: 'transition-all duration-60 ease-standard',      // 60ms - hover states

    // Fast transitions (exit animations, quick state changes)
    fast: 'transition-all duration-120 ease-standard',       // 120ms - fade-out, slide-out

    // Standard transitions (canonical, entry animations)
    standard: 'transition-all duration-240 ease-standard',   // 240ms - fade-in, slide-in, canonical ✅

    // Slow transitions (page transitions, complex animations)
    slow: 'transition-all duration-480 ease-standard',      // 480ms - page transitions

    // Specific Animations (entry)
    fadeIn: 'animate-in fade-in duration-240 ease-standard',
    slideUp: 'animate-in slide-in-from-bottom-2 duration-240 ease-standard',
    slideDown: 'animate-in slide-in-from-top-2 duration-240 ease-standard',
    scaleIn: 'animate-in zoom-in-95 duration-240 ease-standard',

    // Specific Animations (exit)
    fadeOut: 'animate-out fade-out duration-120 ease-standard',
    slideOut: 'animate-out slide-out-to-bottom-2 duration-120 ease-standard',
    scaleOut: 'animate-out zoom-out-95 duration-120 ease-standard',

    // Reduced Motion Support
    reduced: 'motion-reduce:duration-0 motion-reduce:transition-none',
  },

  // Light-Dark Harmony: Calm / Action / Alert Levels
  // Using CSS variables for automatic dark mode support
  harmony: {
    calm: {
      bg: 'bg-card',                                      // Calm backgrounds (auto dark mode)
      border: 'border-border',                            // Calm borders (auto dark mode)
      fg: 'text-muted-foreground',                        // Calm text (auto dark mode)
      shadow: 'shadow-sm',                                // Calm shadows
    },
    action: {
      bg: 'bg-card',                                      // Action backgrounds (auto dark mode)
      border: 'border-border',                            // Action borders (auto dark mode)
      fg: 'text-foreground',                              // Action text (auto dark mode)
      shadow: 'shadow-md',                                // Action shadows
    },
    alert: {
      bg: 'bg-primary/10',                                // Alert backgrounds (auto dark mode)
      border: 'border-primary/20',                        // Alert borders (auto dark mode)
      fg: 'text-primary',                                 // Alert text (auto dark mode)
      shadow: 'shadow-sm',                                // Alert shadows
    },
  },

  // Loading & Preloader States
  // Used by AdminPreloader component for consistent authentication flows
  preloader: {
    // Container styling
    container: 'min-h-screen bg-background flex items-center justify-center',
    content: 'flex flex-col items-center gap-4 p-8 text-center max-w-md',

    // Icon sizes (standardized across all states)
    iconSize: 'h-6 w-6',
    iconSpinner: 'h-6 w-6 animate-spin text-primary',
    iconSuccess: 'h-6 w-6 text-green-500',
    iconProcessing: 'h-5 w-5 text-muted-foreground',

    // Typography
    title: 'text-lg font-semibold text-foreground',
    message: 'text-sm',
    messageDefault: 'text-muted-foreground',
    messagePrimary: 'text-primary',
    messageSuccess: 'text-green-500',

    // Card (for processing state)
    card: 'mt-6 p-4 rounded-lg border border-border bg-surface shadow-sm max-w-sm w-full',
    cardContent: 'flex items-start gap-3',
    cardTitle: 'text-sm font-medium text-foreground',
    cardSubtitle: 'text-xs text-muted-foreground mt-1',

    // Spacing (golden ratio aligned)
    spacing: {
      iconMargin: 'mb-2',
      contentGap: 'gap-4',
      cardGap: 'gap-3',
      cardMarginTop: 'mt-6',
    },
  },

  // Sidebar Design Tokens (Glassmorphism & Premium Effects)
  // Matching BalanceCard visual style for design consistency
  sidebar: {
    // Glassmorphism effect (subtle, matching Card style)
    glass: 'bg-background/60 backdrop-blur-xl',  // Subtle glassmorphism (lighter than modal)

    // Shadow (lighter than Card, but still premium)
    shadow: 'shadow-[0_18px_45px_-24px_rgba(15,23,42,0.25)]',  // Premium shadow (lighter than Card's 0.35)

    // Border (semi-transparent, matching Card style)
    border: 'border border-border/40',  // Subtle border (lighter than Card's 0.60)

    // Radius (matching Card's rounded-2xl)
    radius: 'rounded-2xl',  // Large radius for premium feel

    // Combined glassmorphism container (all effects together)
    container: 'bg-background/60 backdrop-blur-xl shadow-[0_18px_45px_-24px_rgba(15,23,42,0.25)] border border-border/40 rounded-2xl transition-shadow duration-240 ease-standard',

    // Spacing (matching Card padding)
    spacing: {
      header: 'p-6',  // Header padding (matching CardHeader)
      content: 'p-6',  // Content padding (matching CardContent)
      footer: 'p-6',  // Footer padding
    },
  },

  // ============================================
  // LIQUID GLASS MATERIAL PRESETS (Apple iOS 26 Style)
  // ============================================
  // Canonical glass materials for fintech applications
  // Reference: .claude/skills/liquid-glass-fintech/SKILL.md
  // 
  // Material Tiers:
  // - Chrome (85-95%): Navigation, tab bars
  // - Surface (70-85%): Cards, panels ✅ CANONICAL
  // - Elevated (75-88%): Floating elements
  // - Sheet (90-95%): Bottom sheets
  // - Modal (92-98%): Dialogs, critical UI
  // - Subtle (60%): Background layers
  //
  // Each tier includes: background, blur, border, shadow, combined
  // Dark mode variants available in glass.dark.*
  glass: {
    // Material Tier 1: Chrome (85-95% opacity)
    // Navigation, tab bars, toolbars
    chrome: {
      background: 'bg-gradient-to-br from-white/92 via-white/88 to-white/92',
      blur: 'backdrop-blur-xl backdrop-saturate-[180%]',  // 16px blur
      border: 'border border-white/25',
      shadow: 'shadow-[0_1px_0_hsl(0_0%_100%/0.8)_inset]',
      combined: 'bg-gradient-to-br from-white/92 via-white/88 to-white/92 backdrop-blur-xl backdrop-saturate-[180%] border border-white/25 shadow-[0_1px_0_hsl(0_0%_100%/0.8)_inset]',
    },

    // Material Tier 2: Surface (70-85% opacity) ✅ CANONICAL
    // Cards, panels, containers
    surface: {
      background: 'bg-gradient-to-br from-white/85 via-white/70 to-white/85',
      blur: 'backdrop-blur-lg backdrop-saturate-[180%]',  // 12px blur (canonical)
      border: 'border border-white/18',
      shadow: 'shadow-[0_8px_32px_hsl(220_15%_5%/0.08),inset_0_1px_0_hsl(0_0%_100%/0.5)]',
      combined: 'bg-gradient-to-br from-white/85 via-white/70 to-white/85 backdrop-blur-lg backdrop-saturate-[180%] border border-white/18 shadow-[0_8px_32px_hsl(220_15%_5%/0.08),inset_0_1px_0_hsl(0_0%_100%/0.5)]',
    },

    // Material Tier 3: Elevated (75-88% opacity)
    // Floating action buttons, quick actions
    elevated: {
      background: 'bg-gradient-to-br from-white/88 via-white/75 to-white/88',
      blur: 'backdrop-blur-lg backdrop-saturate-[180%]',  // 14px blur
      border: 'border border-white/22',
      shadow: 'shadow-[0_12px_40px_hsl(220_15%_5%/0.12),inset_0_1px_0_hsl(0_0%_100%/0.6)]',
      combined: 'bg-gradient-to-br from-white/88 via-white/75 to-white/88 backdrop-blur-lg backdrop-saturate-[180%] border border-white/22 shadow-[0_12px_40px_hsl(220_15%_5%/0.12),inset_0_1px_0_hsl(0_0%_100%/0.6)]',
    },

    // Material Tier 4: Sheet (90-95% opacity)
    // Bottom sheets, slide-up panels
    sheet: {
      background: 'bg-gradient-to-b from-white/95 to-white/90',
      blur: 'backdrop-blur-xl backdrop-saturate-[180%]',  // 20px blur
      border: 'border-t border-white/30',
      shadow: 'shadow-[0_-8px_40px_hsl(220_15%_5%/0.15)]',
      combined: 'bg-gradient-to-b from-white/95 to-white/90 backdrop-blur-xl backdrop-saturate-[180%] border-t border-white/30 shadow-[0_-8px_40px_hsl(220_15%_5%/0.15)]',
    },

    // Material Tier 4.5: Sheet Side Panel (85-90% opacity)
    // Side panels (left/right), wizards, dialogs
    sheetSide: {
      background: 'bg-gradient-to-br from-white/90 via-white/85 to-white/90',
      blur: 'backdrop-blur-xl backdrop-saturate-[180%]',  // 20px blur
      border: 'border-white/30',
      shadow: 'shadow-[0_8px_32px_hsl(220_15%_5%/0.15)]',
      combined: 'bg-gradient-to-br from-white/90 via-white/85 to-white/90 backdrop-blur-xl backdrop-saturate-[180%] border-white/30 shadow-[0_8px_32px_hsl(220_15%_5%/0.15)]',
    },

    // Material Tier 5: Modal (92-98% opacity)
    // Confirmation dialogs, PIN entry, critical UI
    modal: {
      background: 'bg-gradient-to-b from-white/98 to-white/92',
      blur: 'backdrop-blur-xl backdrop-saturate-[180%]',  // 24px blur
      border: 'border border-white/40',
      shadow: 'shadow-[0_25px_60px_-12px_hsl(220_15%_5%/0.30)]',
      combined: 'bg-gradient-to-b from-white/98 to-white/92 backdrop-blur-xl backdrop-saturate-[180%] border border-white/40 shadow-[0_25px_60px_-12px_hsl(220_15%_5%/0.30)]',
    },

    // Material Tier 6: Subtle (60% opacity)
    // Minimal glass effect, background layers
    subtle: {
      background: 'bg-white/60',
      blur: 'backdrop-blur-md backdrop-saturate-[150%]',  // 8px blur
      border: 'border border-black/5',
      shadow: 'shadow-[0_1px_3px_hsl(220_15%_5%/0.04)]',
      combined: 'bg-white/60 backdrop-blur-md backdrop-saturate-[150%] border border-black/5 shadow-[0_1px_3px_hsl(220_15%_5%/0.04)]',
    },

    // Specular Highlight (top edge gradient)
    // Required for all glass surfaces
    specular: 'absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent',

    // Dark Mode Materials
    // Note: Gradients use CSS variables via hsl() for automatic dark mode support
    // Solid colors use semantic tokens (bg-card, bg-background, bg-muted) for consistency
    dark: {
      chrome: {
        // Using CSS variables for gradients (hsl(var(--card)) via arbitrary values)
        background: 'bg-gradient-to-br from-[hsl(var(--card)/0.85)] via-[hsl(var(--card)/0.80)] to-[hsl(var(--card)/0.85)]',
        blur: 'backdrop-blur-xl backdrop-saturate-[150%] brightness-90',
        border: 'border border-border/40',
        shadow: 'shadow-[0_1px_0_hsl(0_0%_100%/0.08)_inset]',
        combined: 'bg-gradient-to-br from-[hsl(var(--card)/0.85)] via-[hsl(var(--card)/0.80)] to-[hsl(var(--card)/0.85)] backdrop-blur-xl backdrop-saturate-[150%] brightness-90 border border-border/40 shadow-[0_1px_0_hsl(0_0%_100%/0.08)_inset]',
      },
      surface: {
        // Using CSS variables for gradients
        background: 'bg-gradient-to-br from-[hsl(var(--card)/0.75)] via-[hsl(var(--card)/0.60)] to-[hsl(var(--card)/0.75)]',
        blur: 'backdrop-blur-lg backdrop-saturate-[150%] brightness-90',
        border: 'border border-border/30',
        shadow: 'shadow-[0_8px_32px_hsl(0_0%_0%/0.4),inset_0_1px_0_hsl(0_0%_100%/0.1)]',
        combined: 'bg-gradient-to-br from-[hsl(var(--card)/0.75)] via-[hsl(var(--card)/0.60)] to-[hsl(var(--card)/0.75)] backdrop-blur-lg backdrop-saturate-[150%] brightness-90 border border-border/30 shadow-[0_8px_32px_hsl(0_0%_0%/0.4),inset_0_1px_0_hsl(0_0%_100%/0.1)]',
        // Dark mode glassmorphism utilities (using semantic tokens)
        bg30: 'dark:bg-card/30', // Whisper tier opacity
        bg50: 'dark:bg-background/50', // Surface tier opacity
        bg60: 'dark:bg-card/60', // Button tier opacity
        bg60Hover: 'dark:hover:bg-card/80', // Button hover tier opacity
        border30: 'dark:border-border/30', // Border opacity
        // Dark mode solid colors (using semantic tokens)
        bgSolid: 'dark:bg-card', // Solid dark background
        bgSolidHover: 'dark:group-hover:bg-muted', // Solid dark hover
        bgOverlay: 'dark:bg-background/60', // Dark overlay (darker variant)
      },
      elevated: {
        background: 'bg-gradient-to-br from-[hsl(var(--card)/0.82)] via-[hsl(var(--background)/0.70)] to-[hsl(var(--card)/0.82)]',
        blur: 'backdrop-blur-lg backdrop-saturate-[150%] brightness-92',
        border: 'border border-border/40',
        shadow: 'shadow-[0_12px_40px_hsl(0_0%_0%/0.5),inset_0_1px_0_hsl(0_0%_100%/0.08)]',
        combined: 'bg-gradient-to-br from-[hsl(var(--card)/0.82)] via-[hsl(var(--background)/0.70)] to-[hsl(var(--card)/0.82)] backdrop-blur-lg backdrop-saturate-[150%] brightness-92 border border-border/40 shadow-[0_12px_40px_hsl(0_0%_0%/0.5),inset_0_1px_0_hsl(0_0%_100%/0.08)]',
      },
      sheet: {
        background: 'bg-gradient-to-b from-[hsl(var(--card)/0.92)] to-[hsl(var(--background)/0.88)]',
        blur: 'backdrop-blur-xl backdrop-saturate-[150%] brightness-92',
        border: 'border-t border-border/50',
        shadow: 'shadow-[0_-8px_40px_hsl(0_0%_0%/0.5)]',
        combined: 'bg-gradient-to-b from-[hsl(var(--card)/0.92)] to-[hsl(var(--background)/0.88)] backdrop-blur-xl backdrop-saturate-[150%] brightness-92 border-t border-border/50 shadow-[0_-8px_40px_hsl(0_0%_0%/0.5)]',
      },
      sheetSide: {
        background: 'dark:bg-gradient-to-br dark:from-[hsl(var(--card)/0.85)] dark:via-[hsl(var(--card)/0.75)] dark:to-[hsl(var(--card)/0.85)]',
        blur: 'dark:backdrop-blur-xl dark:backdrop-saturate-[150%] dark:brightness-90',
        border: 'dark:border-white/15',
        shadow: 'dark:shadow-[0_8px_32px_hsl(0_0%_0%/0.5)]',
        combined: 'dark:bg-gradient-to-br dark:from-[hsl(var(--card)/0.85)] dark:via-[hsl(var(--card)/0.75)] dark:to-[hsl(var(--card)/0.85)] dark:backdrop-blur-xl dark:backdrop-saturate-[150%] dark:brightness-90 dark:border-white/15 dark:shadow-[0_8px_32px_hsl(0_0%_0%/0.5)]',
      },
      modal: {
        background: 'bg-gradient-to-b from-[hsl(var(--card)/0.95)] to-[hsl(var(--background)/0.90)]',
        blur: 'backdrop-blur-xl backdrop-saturate-[150%] brightness-95',
        border: 'border border-border/60',
        shadow: 'shadow-[0_25px_60px_hsl(0_0%_0%/0.6),inset_0_1px_0_hsl(0_0%_100%/0.12)]',
        combined: 'bg-gradient-to-b from-[hsl(var(--card)/0.95)] to-[hsl(var(--background)/0.90)] backdrop-blur-xl backdrop-saturate-[150%] brightness-95 border border-border/60 shadow-[0_25px_60px_hsl(0_0%_0%/0.6),inset_0_1px_0_hsl(0_0%_100%/0.12)]',
      },
      specular: 'dark:absolute dark:inset-x-0 dark:top-0 dark:h-px dark:bg-gradient-to-r dark:from-transparent dark:via-white/20 dark:to-transparent',
    },
  },

  // ============================================
  // HERO BACKGROUND GRADIENTS (Stripe-style)
  // ============================================
  // Subtle background gradients for hero sections.
  // Create depth without overwhelming content.
  // Reference: Brand Book v1.5.0
  //
  // Usage:
  //   <div style={{ background: semanticTokens.heroGradient.lavenderGlow.css }}>
  //   OR
  //   <div className={semanticTokens.heroGradient.lavenderGlow.tailwind}>
  //
  heroGradient: {
    // Lavender Glow — "Send Money Globally"
    // ENHANCED: More visible purple-blue radials, Stripe/Linear style
    // Opacity increased from 0.15/0.10 → 0.28/0.20 for intentional design presence
    lavenderGlow: {
      css: `radial-gradient(ellipse at 15% 40%, hsla(270, 95%, 75%, 0.28) 0%, transparent 55%),
            radial-gradient(ellipse at 85% 70%, hsla(217, 91%, 60%, 0.20) 0%, transparent 55%),
            radial-gradient(ellipse at 50% 90%, hsla(240, 80%, 70%, 0.12) 0%, transparent 50%),
            hsl(0, 0%, 100%)`,
      tailwind: 'bg-[radial-gradient(ellipse_at_15%_40%,hsla(270,95%,75%,0.28)_0%,transparent_55%),radial-gradient(ellipse_at_85%_70%,hsla(217,91%,60%,0.20)_0%,transparent_55%),radial-gradient(ellipse_at_50%_90%,hsla(240,80%,70%,0.12)_0%,transparent_50%),hsl(0,0%,100%)]',
      description: 'Enhanced purple-blue radials — premium fintech',
    },

    // Blue-Purple Mesh — "Crypto Meets Banking"
    // Deeper blue-purple tones for crypto/innovation messaging
    bluePurpleMesh: {
      css: `radial-gradient(ellipse at 30% 20%, hsla(217, 91%, 60%, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 60%, hsla(270, 80%, 65%, 0.10) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 90%, hsla(240, 70%, 60%, 0.08) 0%, transparent 50%),
            hsl(0, 0%, 100%)`,
      tailwind: 'bg-[radial-gradient(ellipse_at_30%_20%,hsla(217,91%,60%,0.12)_0%,transparent_50%),radial-gradient(ellipse_at_70%_60%,hsla(270,80%,65%,0.10)_0%,transparent_50%),radial-gradient(ellipse_at_50%_90%,hsla(240,70%,60%,0.08)_0%,transparent_50%),hsl(0,0%,100%)]',
      description: 'Blue-purple mesh — crypto innovation',
    },

    // Mint Fresh (Cash App style) — "Get Paid Instantly"
    // Fresh green tones for speed/money/success messaging
    mintFresh: {
      css: `radial-gradient(ellipse at 60% 30%, hsla(160, 84%, 39%, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 20% 70%, hsla(150, 70%, 50%, 0.06) 0%, transparent 50%),
            linear-gradient(to bottom, hsl(0, 0%, 100%), hsl(150, 20%, 98%))`,
      tailwind: 'bg-[radial-gradient(ellipse_at_60%_30%,hsla(160,84%,39%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_20%_70%,hsla(150,70%,50%,0.06)_0%,transparent_50%),linear-gradient(to_bottom,hsl(0,0%,100%),hsl(150,20%,98%))]',
      description: 'Mint fresh — speed, money, success',
    },

    // Supabank Blue — Primary brand gradient
    // For main CTAs and primary hero sections
    supabankBlue: {
      css: `radial-gradient(ellipse at 50% 50%, hsla(222, 59%, 58%, 0.12) 0%, transparent 60%),
            radial-gradient(ellipse at 80% 20%, hsla(222, 70%, 65%, 0.08) 0%, transparent 50%),
            hsl(0, 0%, 100%)`,
      tailwind: 'bg-[radial-gradient(ellipse_at_50%_50%,hsla(222,59%,58%,0.12)_0%,transparent_60%),radial-gradient(ellipse_at_80%_20%,hsla(222,70%,65%,0.08)_0%,transparent_50%),hsl(0,0%,100%)]',
      description: 'Supabank Blue — primary brand',
    },

    // Dark Mode Variants
    dark: {
      lavenderGlow: {
        // Using CSS variable for background color (automatic dark mode support)
        css: `radial-gradient(ellipse at 20% 50%, hsla(270, 95%, 75%, 0.08) 0%, transparent 50%),
              radial-gradient(ellipse at 80% 80%, hsla(217, 91%, 60%, 0.06) 0%, transparent 50%),
              hsl(var(--background))`,
        tailwind: 'bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]',
      },
      bluePurpleMesh: {
        // Using CSS variable for background color (automatic dark mode support)
        css: `radial-gradient(ellipse at 30% 20%, hsla(217, 91%, 60%, 0.08) 0%, transparent 50%),
              radial-gradient(ellipse at 70% 60%, hsla(270, 80%, 65%, 0.06) 0%, transparent 50%),
              hsl(var(--background))`,
        tailwind: 'bg-[radial-gradient(ellipse_at_30%_20%,hsla(217,91%,60%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_70%_60%,hsla(270,80%,65%,0.06)_0%,transparent_50%),hsl(var(--background))]',
      },
      mintFresh: {
        // Using CSS variable for background color (automatic dark mode support)
        css: `radial-gradient(ellipse at 60% 30%, hsla(160, 84%, 39%, 0.06) 0%, transparent 50%),
              radial-gradient(ellipse at 20% 70%, hsla(150, 70%, 50%, 0.04) 0%, transparent 50%),
              hsl(var(--background))`,
        tailwind: 'bg-[radial-gradient(ellipse_at_60%_30%,hsla(160,84%,39%,0.06)_0%,transparent_50%),radial-gradient(ellipse_at_20%_70%,hsla(150,70%,50%,0.04)_0%,transparent_50%),hsl(var(--background))]',
      },
    },
  },

  // ============================================================================
  // TEXT GRADIENTS — For bg-clip-text text-transparent
  // ============================================================================
  // Usage:
  //   <h1 className={cn("bg-clip-text text-transparent", semanticTokens.textGradient.hero)}>
  //
  // IMPORTANT: These are SOLID gradients for text, not translucent backgrounds.
  // heroGradient above is for section backgrounds (low opacity).
  // textGradient below is for text (full opacity, high contrast).
  //
  textGradient: {
    // Hero Headline — Bold blue gradient for main headlines
    // Supabank brand blue with slight purple shift
    hero: 'bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600',
    heroDark: 'bg-gradient-to-r from-blue-400 via-blue-300 to-indigo-400',

    // Tagline — Purple-blue gradient for secondary hero text
    // Medium saturation for visibility and premium feel
    tagline: 'bg-gradient-to-r from-indigo-600 via-purple-500 to-blue-600',
    taglineDark: 'bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400',

    // Success — Green gradient for positive messaging
    success: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    successDark: 'bg-gradient-to-r from-emerald-400 to-teal-400',

    // Premium — Dark luxury gradient
    premium: 'bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700',
    premiumDark: 'bg-gradient-to-r from-slate-300 via-slate-200 to-slate-300',
  },
} as const;

// Type definitions for strict typing
export type SemanticToken = typeof semanticTokens;
export type ForegroundToken = keyof typeof semanticTokens.fg;
export type BackgroundToken = keyof typeof semanticTokens.bg;
export type TypographyToken = keyof typeof semanticTokens.typography;
export type GapToken = keyof typeof semanticTokens.gap;
export type StackToken = keyof typeof semanticTokens.stack;

/**
 * Utility function to get semantic token classes
 * Usage: getToken('fg', 'default') returns 'text-slate-900'
 */
export function getToken<T extends keyof SemanticToken>(
  category: T,
  variant: keyof SemanticToken[T]
): string {
  return semanticTokens[category][variant] as string;
}

/**
 * Component-specific token bundles
 * Common combinations for different component types
 */
export const componentTokens = {
  // Button variants (Full styling with correct text colors)
  // IMPORTANT: Always use button.primary for CTAs (includes white text)
  button: {
    primary: `${semanticTokens.button.primary} ${semanticTokens.shadow.sm} ${semanticTokens.radius.md} ${semanticTokens.typography.buttonText} ${semanticTokens.button.primaryHover} ${semanticTokens.motion.fast}`,
    secondary: `${semanticTokens.button.secondary} ${semanticTokens.shadow.sm} ${semanticTokens.radius.md} ${semanticTokens.typography.buttonText} ${semanticTokens.button.secondaryHover} ${semanticTokens.motion.fast}`,
    ghost: `${semanticTokens.fg.default} ${semanticTokens.typography.buttonText} hover:${semanticTokens.bg.muted} ${semanticTokens.radius.md} ${semanticTokens.motion.fast}`,
    // Icon-only micro buttons (for clear/close icons in inputs)
    iconMicro: `${semanticTokens.size.icon.xs} p-0`,           // h-3 w-3 p-0
    iconSmall: `${semanticTokens.size.icon.sm} p-0`,           // h-4 w-4 p-0
    // Hover states for icon buttons
    iconMicroHover: `${semanticTokens.size.icon.xs} p-0 hover:bg-primary/20`,
    iconSmallHover: `${semanticTokens.size.icon.sm} p-0 hover:bg-muted`,
  },

  // Input variants
  input: {
    default: `${semanticTokens.bg.surface} ${semanticTokens.border.default} ${semanticTokens.radius.md} ${semanticTokens.typography.body} focus:${semanticTokens.shadow.focus} focus:${semanticTokens.border.focus} ${semanticTokens.motion.fast}`,
    error: `${semanticTokens.bg.surface} ${semanticTokens.danger.border} ${semanticTokens.radius.md} ${semanticTokens.typography.body} focus:ring-red-500 focus:border-red-500`,
    // Input with icon padding (use on <Input> when icon is present)
    withIconLeft: 'pl-10',   // 40px left padding for search icon
    withIconRight: 'pr-8',   // 32px right padding for clear button
    withIconBoth: 'pl-10 pr-8', // Both sides
  },

  // Status indicator dots (for connection status, activity, etc.)
  statusDot: {
    sm: `${semanticTokens.size.dot.sm} ${semanticTokens.radius.full}`,  // w-2 h-2 rounded-full
    md: `${semanticTokens.size.dot.md} ${semanticTokens.radius.full}`,  // w-3 h-3 rounded-full
    // With pulse animation
    smPulse: `${semanticTokens.size.dot.sm} ${semanticTokens.radius.full} animate-pulse`,
    mdPulse: `${semanticTokens.size.dot.md} ${semanticTokens.radius.full} animate-pulse`,
  },

  // Card variants  
  card: {
    default: `${semanticTokens.bg.surface} ${semanticTokens.border.default} ${semanticTokens.shadow.sm} ${semanticTokens.radius.lg}`,
    raised: `${semanticTokens.bg.raised} ${semanticTokens.border.subtle} ${semanticTokens.shadow.md} ${semanticTokens.radius.lg}`,
  },

  // Alert variants
  alert: {
    success: `${semanticTokens.positive.bg} ${semanticTokens.positive.border} ${semanticTokens.positive.fg} ${semanticTokens.radius.md}`,
    warning: `${semanticTokens.warning.bg} ${semanticTokens.warning.border} ${semanticTokens.warning.fg} ${semanticTokens.radius.md}`,
    danger: `${semanticTokens.danger.bg} ${semanticTokens.danger.border} ${semanticTokens.danger.fg} ${semanticTokens.radius.md}`,
    premium: `${semanticTokens.premium.bg} ${semanticTokens.premium.border} ${semanticTokens.premium.fg} ${semanticTokens.radius.md}`, // VIP/Premium tier alerts
  },

  table: {
    container: `border ${semanticTokens.bg.surface} ${semanticTokens.border.default} ${semanticTokens.radius.lg}`,
    headerText: `${semanticTokens.typography.body} ${semanticTokens.fg.muted}`, cellText: `${semanticTokens.typography.body} ${semanticTokens.fg.default}`,
    rowInteractive: `hover:${semanticTokens.bg.muted} ${semanticTokens.motion.fast}`, rowSelected: `${semanticTokens.bg.muted}`,
    emptyIcon: `${semanticTokens.fg.muted}`, emptyText: `${semanticTokens.typography.body} ${semanticTokens.fg.muted}`,
  },

  badge: {
    status: {
      success: `${semanticTokens.positive.bg} ${semanticTokens.positive.fg}`, warning: `${semanticTokens.warning.bg} ${semanticTokens.warning.fg}`,
      danger: `${semanticTokens.danger.bg} ${semanticTokens.danger.fg}`, info: `${semanticTokens.info.bg} ${semanticTokens.info.fg}`,
      neutral: `${semanticTokens.bg.muted} ${semanticTokens.fg.default}`,
      premium: `${semanticTokens.premium.bg} ${semanticTokens.premium.fg}`, // Black Luxury (VIP/Premium tier)
    },
  },

  form: {
    label: `${semanticTokens.typography.label} ${semanticTokens.fg.default}`,
    control: `border ${semanticTokens.bg.surface} ${semanticTokens.border.default} ${semanticTokens.radius.md} ${semanticTokens.typography.body} focus:${semanticTokens.shadow.focus} focus:${semanticTokens.border.focus} ${semanticTokens.motion.fast} disabled:opacity-50 disabled:pointer-events-none`,
    helper: `${semanticTokens.typography.caption} ${semanticTokens.fg.muted}`,
  },

  filterPanel: {
    container: `border ${semanticTokens.bg.surface} ${semanticTokens.border.default} ${semanticTokens.radius.lg}`,
    divider: `${semanticTokens.border.subtle}`, summary: `${semanticTokens.typography.caption} ${semanticTokens.fg.muted}`,
  },
} as const;

/**
 * Fallback hex values for SSR and external libraries
 * These values correspond to the default theme colors
 * Used when CSS custom properties are not available (SSR, QR code generation, etc.)
 */
export const themeFallbacks = {
  // Foreground colors (from --foreground: 222 22% 10%)
  foreground: '#1a1a1a', // hsl(222 22% 10%) converted to hex

  // Background colors (from --background: 0 0% 100%)
  background: '#ffffff', // hsl(0 0% 100%) = white

  // Primary color - Supabank Blue (Brand Book SSOT)
  primary: '#3B82F6', // hsl(221 83% 53%) = Supabank Blue (Apple Blue)

  // Muted foreground (from --muted-foreground: 215 16% 47%)
  mutedForeground: '#64748b', // hsl(215 16% 47%) = slate-600

  // Payment rail brand colors (official brand guidelines)
  // These are fixed brand colors that must remain consistent
  paymentRails: {
    sepa: '#2566AF', // SEPA official brand color
    spei: '#002855', // SPEI official brand color
  },

  // Cloud Dancer / Zen State (Pantone 2026)
  // Used for onboarding, success screens, premium welcome
  zen: {
    cloudDancer: '#F0EFEA', // Primary zen background
    airWhite: '#F8F7F4',    // Elevated zen surface
    shadowPine: '#132B24',  // S glyph color
    obsidian: '#0D1F1A',    // "supabank" text
    mossedStone: '#8A9A8B', // "xyz" text (muted)
    border: '#E5E4DF',      // Subtle zen border
  },
} as const;

/**
 * Blockchain Network Brand Colors
 * 
 * Official brand colors for supported blockchain networks.
 * These are fixed brand colors that must remain consistent with official branding.
 * 
 * Usage: Use these tokens instead of hardcoded hex values in components.
 * 
 * CANON Compliance: Centralized in theme.ts (SSOT)
 */
export const networkBrandColors = {
  ethereum: {
    hex: '#627EEA',
    bg: 'bg-[#627EEA]',
    text: 'text-white',
  },
  base: {
    hex: '#0052FF',
    bg: 'bg-[#0052FF]',
    text: 'text-white',
  },
  solana: {
    hex: '#9945FF', // Primary color (gradient uses #9945FF → #14F195)
    bg: 'bg-gradient-to-br from-[#9945FF] to-[#14F195]',
    text: 'text-white',
  },
  polygon: {
    hex: '#8247E5',
    bg: 'bg-[#8247E5]',
    text: 'text-white',
  },
  arbitrum: {
    hex: '#28A0F0',
    bg: 'bg-[#28A0F0]',
    text: 'text-white',
  },
  optimism: {
    hex: '#FF0420',
    bg: 'bg-[#FF0420]',
    text: 'text-white',
  },
  avalanche: {
    hex: '#E84142',
    bg: 'bg-[#E84142]',
    text: 'text-white',
  },
  stellar: {
    hex: '#000000',
    bg: 'bg-[#000000]',
    text: 'text-white',
  },
  tron: {
    hex: '#FF0013',
    bg: 'bg-[#FF0013]',
    text: 'text-white',
  },
  // Fallback for unknown networks
  unknown: {
    hex: '#64748b', // slate-500
    bg: 'bg-slate-500',
    text: 'text-white',
  },
} as const;

/**
 * Network brand color type
 */
export type NetworkBrandColor = {
  readonly hex: string;
  readonly bg: string;
  readonly text: string;
};

/**
 * Get network brand color configuration
 * @param network - Network identifier (lowercase)
 * @returns Network color config with bg, text, and hex
 */
export function getNetworkBrandColor(network: string): NetworkBrandColor {
  const normalizedNetwork = network.toLowerCase();
  return (networkBrandColors[normalizedNetwork as keyof typeof networkBrandColors]
    || networkBrandColors.unknown) as NetworkBrandColor;
}

// Export default theme configuration
export default semanticTokens;